import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Building2,
  Plus,
  Pencil,
  Trash2,
  Users,
  UserPlus,
  UserMinus,
  RefreshCw,
  X,
  ChevronDown,
  CheckCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  TODAS_PROPIEDADES,
  CREAR_PROPIEDAD,
  ACTUALIZAR_PROPIEDAD,
  ELIMINAR_PROPIEDAD,
  USUARIOS_PROPIEDAD,
  ASIGNAR_USUARIO_PROPIEDAD,
  REMOVER_USUARIO_PROPIEDAD
} from '../graphql/propiedades';
import { GET_USUARIOS } from '../graphql/configuracion';
import './GestionPropiedades.css';

// ============================================================
// Constants
// ============================================================

const TIMEZONES = [
  'America/Bogota',
  'America/Lima',
  'America/Caracas',
  'America/Santiago',
  'America/Buenos_Aires',
  'America/Sao_Paulo',
  'America/Mexico_City',
  'America/New_York',
  'America/Los_Angeles',
  'Europe/Madrid',
  'UTC'
];

const ROLES_PROPIEDAD = [
  { value: 'admin', label: 'Administrador' },
  { value: 'gerente', label: 'Gerente' },
  { value: 'recepcionista', label: 'Recepcionista' },
  { value: 'limpieza', label: 'Limpieza' },
  { value: 'mantenimiento', label: 'Mantenimiento' }
];

const EMPTY_FORM = {
  nombre: '',
  nombre_corto: '',
  nit: '',
  direccion: '',
  ciudad: '',
  departamento: '',
  telefono: '',
  email: '',
  color_primario: '#1e40af',
  color_secundario: '#3b82f6',
  timezone: 'America/Bogota'
};

// ============================================================
// Helper components
// ============================================================

function EstadoBadge({ activo }) {
  return (
    <span className={`gp__badge gp__badge--${activo ? 'activo' : 'inactivo'}`}>
      {activo ? <CheckCircle size={11} /> : <XCircle size={11} />}
      {activo ? 'Activo' : 'Inactivo'}
    </span>
  );
}

function RolBadge({ rol }) {
  return (
    <span className={`gp__badge gp__badge--rol gp__badge--rol-${rol}`}>
      {rol}
    </span>
  );
}

// ============================================================
// Modal: Crear / Editar Propiedad
// ============================================================

function ModalPropiedad({ propiedad, onClose, onSaved }) {
  const esEdicion = Boolean(propiedad);
  const [form, setForm] = useState(
    esEdicion
      ? {
          nombre: propiedad.nombre || '',
          nombre_corto: propiedad.nombre_corto || '',
          nit: propiedad.nit || '',
          direccion: propiedad.direccion || '',
          ciudad: propiedad.ciudad || '',
          departamento: propiedad.departamento || '',
          telefono: propiedad.telefono || '',
          email: propiedad.email || '',
          color_primario: propiedad.color_primario || '#1e40af',
          color_secundario: propiedad.color_secundario || '#3b82f6',
          timezone: propiedad.timezone || 'America/Bogota'
        }
      : { ...EMPTY_FORM }
  );

  const [crearPropiedad, { loading: creando }] = useMutation(CREAR_PROPIEDAD, {
    refetchQueries: [{ query: TODAS_PROPIEDADES }],
    onCompleted: () => {
      toast.success('Propiedad creada correctamente');
      onSaved();
    },
    onError: (err) => toast.error('Error al crear propiedad: ' + err.message)
  });

  const [actualizarPropiedad, { loading: actualizando }] = useMutation(ACTUALIZAR_PROPIEDAD, {
    refetchQueries: [{ query: TODAS_PROPIEDADES }],
    onCompleted: () => {
      toast.success('Propiedad actualizada correctamente');
      onSaved();
    },
    onError: (err) => toast.error('Error al actualizar propiedad: ' + err.message)
  });

  const cargando = creando || actualizando;

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }

    const input = {
      nombre: form.nombre.trim(),
      nombre_corto: form.nombre_corto.trim() || null,
      nit: form.nit.trim() || null,
      direccion: form.direccion.trim() || null,
      ciudad: form.ciudad.trim() || null,
      departamento: form.departamento.trim() || null,
      telefono: form.telefono.trim() || null,
      email: form.email.trim() || null,
      color_primario: form.color_primario || null,
      color_secundario: form.color_secundario || null,
      timezone: form.timezone || 'America/Bogota'
    };

    if (esEdicion) {
      actualizarPropiedad({ variables: { id: parseInt(propiedad.id), input } });
    } else {
      crearPropiedad({ variables: { input } });
    }
  };

  return (
    <div className="gp__overlay" onClick={onClose}>
      <div className="gp__modal gp__modal--lg" onClick={(e) => e.stopPropagation()}>
        <div className="gp__modal-header">
          <div className="gp__modal-title-row">
            <Building2 size={20} />
            <h2 className="gp__modal-title">
              {esEdicion ? 'Editar Propiedad' : 'Nueva Propiedad'}
            </h2>
          </div>
          <button className="gp__modal-close" onClick={onClose} aria-label="Cerrar">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="gp__modal-body">
            {/* Nombre + Nombre Corto */}
            <div className="gp__field-row">
              <div className="gp__field">
                <label htmlFor="gp-nombre">Nombre *</label>
                <input
                  id="gp-nombre"
                  type="text"
                  value={form.nombre}
                  onChange={(e) => set('nombre', e.target.value)}
                  placeholder="Ej: Hotel Las Palmas"
                  required
                />
              </div>
              <div className="gp__field">
                <label htmlFor="gp-nombre-corto">Nombre Corto</label>
                <input
                  id="gp-nombre-corto"
                  type="text"
                  value={form.nombre_corto}
                  onChange={(e) => set('nombre_corto', e.target.value)}
                  placeholder="Ej: Palmas"
                />
              </div>
            </div>

            {/* NIT + Telefono */}
            <div className="gp__field-row">
              <div className="gp__field">
                <label htmlFor="gp-nit">NIT</label>
                <input
                  id="gp-nit"
                  type="text"
                  value={form.nit}
                  onChange={(e) => set('nit', e.target.value)}
                  placeholder="Ej: 900123456-1"
                />
              </div>
              <div className="gp__field">
                <label htmlFor="gp-telefono">Telefono</label>
                <input
                  id="gp-telefono"
                  type="text"
                  value={form.telefono}
                  onChange={(e) => set('telefono', e.target.value)}
                  placeholder="Ej: 6012345678"
                />
              </div>
            </div>

            {/* Email */}
            <div className="gp__field">
              <label htmlFor="gp-email">Email</label>
              <input
                id="gp-email"
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                placeholder="contacto@hotel.com"
              />
            </div>

            {/* Direccion */}
            <div className="gp__field">
              <label htmlFor="gp-direccion">Direccion</label>
              <input
                id="gp-direccion"
                type="text"
                value={form.direccion}
                onChange={(e) => set('direccion', e.target.value)}
                placeholder="Calle 10 # 5 - 20"
              />
            </div>

            {/* Ciudad + Departamento */}
            <div className="gp__field-row">
              <div className="gp__field">
                <label htmlFor="gp-ciudad">Ciudad</label>
                <input
                  id="gp-ciudad"
                  type="text"
                  value={form.ciudad}
                  onChange={(e) => set('ciudad', e.target.value)}
                  placeholder="Bogota"
                />
              </div>
              <div className="gp__field">
                <label htmlFor="gp-dpto">Departamento</label>
                <input
                  id="gp-dpto"
                  type="text"
                  value={form.departamento}
                  onChange={(e) => set('departamento', e.target.value)}
                  placeholder="Cundinamarca"
                />
              </div>
            </div>

            {/* Timezone */}
            <div className="gp__field">
              <label htmlFor="gp-tz">Zona Horaria</label>
              <div className="gp__select-wrap">
                <select
                  id="gp-tz"
                  value={form.timezone}
                  onChange={(e) => set('timezone', e.target.value)}
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="gp__select-icon" />
              </div>
            </div>

            {/* Colores */}
            <div className="gp__field-row">
              <div className="gp__field">
                <label htmlFor="gp-color1">Color Primario</label>
                <div className="gp__color-field">
                  <input
                    id="gp-color1"
                    type="color"
                    value={form.color_primario}
                    onChange={(e) => set('color_primario', e.target.value)}
                  />
                  <span className="gp__color-value">{form.color_primario}</span>
                </div>
              </div>
              <div className="gp__field">
                <label htmlFor="gp-color2">Color Secundario</label>
                <div className="gp__color-field">
                  <input
                    id="gp-color2"
                    type="color"
                    value={form.color_secundario}
                    onChange={(e) => set('color_secundario', e.target.value)}
                  />
                  <span className="gp__color-value">{form.color_secundario}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="gp__modal-footer">
            <button type="button" className="gp__btn" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="gp__btn gp__btn--primary" disabled={cargando}>
              {cargando ? (
                <><RefreshCw size={15} className="gp__spin" /> Guardando...</>
              ) : (
                <><CheckCircle size={15} /> {esEdicion ? 'Guardar Cambios' : 'Crear Propiedad'}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// Modal: Confirmar Eliminar
// ============================================================

function ModalConfirmarEliminar({ propiedad, onClose, onConfirm, cargando }) {
  return (
    <div className="gp__overlay" onClick={onClose}>
      <div className="gp__modal gp__modal--sm" onClick={(e) => e.stopPropagation()}>
        <div className="gp__modal-header">
          <div className="gp__modal-title-row">
            <AlertTriangle size={20} className="gp__icon--danger" />
            <h2 className="gp__modal-title">Eliminar Propiedad</h2>
          </div>
          <button className="gp__modal-close" onClick={onClose} aria-label="Cerrar">
            <X size={20} />
          </button>
        </div>
        <div className="gp__modal-body gp__modal-body--center">
          <div className="gp__confirm-icon">
            <Trash2 size={28} />
          </div>
          <p className="gp__confirm-text">
            Esta accion eliminara permanentemente la propiedad{' '}
            <strong>"{propiedad.nombre}"</strong>. Esta operacion no se puede deshacer.
          </p>
        </div>
        <div className="gp__modal-footer">
          <button className="gp__btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="gp__btn gp__btn--danger"
            onClick={onConfirm}
            disabled={cargando}
          >
            {cargando ? (
              <><RefreshCw size={15} className="gp__spin" /> Eliminando...</>
            ) : (
              <><Trash2 size={15} /> Eliminar</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Modal: Asignar Usuario
// ============================================================

function ModalAsignarUsuario({ propiedadId, usuariosAsignados, onClose, onSaved }) {
  const [usuarioId, setUsuarioId] = useState('');
  const [rol, setRol] = useState('recepcionista');

  const { data: dataUsuarios, loading: loadingUsuarios } = useQuery(GET_USUARIOS, {
    variables: { activo: true }
  });

  const [asignarUsuario, { loading: asignando }] = useMutation(ASIGNAR_USUARIO_PROPIEDAD, {
    refetchQueries: [{ query: USUARIOS_PROPIEDAD, variables: { propiedad_id: parseInt(propiedadId) } }],
    onCompleted: () => {
      toast.success('Usuario asignado correctamente');
      onSaved();
    },
    onError: (err) => toast.error('Error al asignar usuario: ' + err.message)
  });

  const idsAsignados = new Set((usuariosAsignados || []).map((u) => String(u.id)));
  const usuariosDisponibles = (dataUsuarios?.usuarios || []).filter(
    (u) => !idsAsignados.has(String(u.id))
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!usuarioId) {
      toast.error('Selecciona un usuario');
      return;
    }
    asignarUsuario({
      variables: {
        usuario_id: parseInt(usuarioId),
        propiedad_id: parseInt(propiedadId),
        rol
      }
    });
  };

  return (
    <div className="gp__overlay" onClick={onClose}>
      <div className="gp__modal gp__modal--sm" onClick={(e) => e.stopPropagation()}>
        <div className="gp__modal-header">
          <div className="gp__modal-title-row">
            <UserPlus size={20} />
            <h2 className="gp__modal-title">Asignar Usuario</h2>
          </div>
          <button className="gp__modal-close" onClick={onClose} aria-label="Cerrar">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="gp__modal-body">
            <div className="gp__field">
              <label htmlFor="gp-usuario-sel">Usuario</label>
              {loadingUsuarios ? (
                <p className="gp__loading-text">Cargando usuarios...</p>
              ) : (
                <div className="gp__select-wrap">
                  <select
                    id="gp-usuario-sel"
                    value={usuarioId}
                    onChange={(e) => setUsuarioId(e.target.value)}
                    required
                  >
                    <option value="">-- Seleccionar usuario --</option>
                    {usuariosDisponibles.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.nombre} {u.apellido} ({u.usuario})
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="gp__select-icon" />
                </div>
              )}
              {!loadingUsuarios && usuariosDisponibles.length === 0 && (
                <p className="gp__field-hint">No hay usuarios disponibles para asignar</p>
              )}
            </div>

            <div className="gp__field">
              <label htmlFor="gp-rol-sel">Rol en esta Propiedad</label>
              <div className="gp__select-wrap">
                <select
                  id="gp-rol-sel"
                  value={rol}
                  onChange={(e) => setRol(e.target.value)}
                >
                  {ROLES_PROPIEDAD.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="gp__select-icon" />
              </div>
            </div>
          </div>

          <div className="gp__modal-footer">
            <button type="button" className="gp__btn" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="submit"
              className="gp__btn gp__btn--primary"
              disabled={asignando || loadingUsuarios || usuariosDisponibles.length === 0}
            >
              {asignando ? (
                <><RefreshCw size={15} className="gp__spin" /> Asignando...</>
              ) : (
                <><UserPlus size={15} /> Asignar</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// Tab 1: Propiedades
// ============================================================

function TabPropiedades() {
  const [modalCrear, setModalCrear] = useState(false);
  const [propiedadEditando, setPropiedadEditando] = useState(null);
  const [propiedadEliminando, setPropiedadEliminando] = useState(null);

  const { data, loading, error } = useQuery(TODAS_PROPIEDADES);

  const [eliminarPropiedad, { loading: eliminando }] = useMutation(ELIMINAR_PROPIEDAD, {
    refetchQueries: [{ query: TODAS_PROPIEDADES }],
    onCompleted: () => {
      toast.success('Propiedad eliminada correctamente');
      setPropiedadEliminando(null);
    },
    onError: (err) => {
      toast.error('Error al eliminar: ' + err.message);
      setPropiedadEliminando(null);
    }
  });

  const handleEliminarConfirm = () => {
    if (!propiedadEliminando) return;
    eliminarPropiedad({ variables: { id: parseInt(propiedadEliminando.id) } });
  };

  if (loading) {
    return (
      <div className="gp__state">
        <RefreshCw size={28} className="gp__spin" />
        <p>Cargando propiedades...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gp__state gp__state--error">
        <XCircle size={28} />
        <p>Error al cargar propiedades: {error.message}</p>
      </div>
    );
  }

  const propiedades = data?.todasPropiedades || [];

  return (
    <>
      <div className="gp__section-header">
        <div>
          <h3 className="gp__section-title">Propiedades registradas</h3>
          <p className="gp__section-subtitle">{propiedades.length} propiedad{propiedades.length !== 1 ? 'es' : ''} en el sistema</p>
        </div>
        <button
          className="gp__btn gp__btn--primary"
          onClick={() => setModalCrear(true)}
        >
          <Plus size={16} />
          Nueva Propiedad
        </button>
      </div>

      {propiedades.length === 0 ? (
        <div className="gp__empty">
          <Building2 size={40} />
          <p className="gp__empty-title">No hay propiedades registradas</p>
          <p className="gp__empty-desc">Crea la primera propiedad del sistema</p>
        </div>
      ) : (
        <div className="gp__table-container">
          <table className="gp__table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Ciudad</th>
                <th>NIT</th>
                <th>Estado</th>
                <th className="gp__th--actions">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {propiedades.map((prop) => (
                <tr key={prop.id}>
                  <td data-label="Nombre">
                    <div className="gp__prop-name-cell">
                      <span
                        className="gp__color-dot"
                        style={{ background: prop.color_primario || '#1e40af' }}
                        title={prop.color_primario}
                      />
                      <span className="gp__prop-nombre">{prop.nombre}</span>
                      {prop.nombre_corto && (
                        <span className="gp__prop-corto">{prop.nombre_corto}</span>
                      )}
                    </div>
                  </td>
                  <td data-label="Ciudad">{prop.ciudad || <span className="gp__empty-val">—</span>}</td>
                  <td data-label="NIT">{prop.nit || <span className="gp__empty-val">—</span>}</td>
                  <td data-label="Estado">
                    <EstadoBadge activo={prop.activo} />
                  </td>
                  <td data-label="Acciones">
                    <div className="gp__actions-cell">
                      <button
                        className="gp__action-btn gp__action-btn--edit"
                        onClick={() => setPropiedadEditando(prop)}
                        title="Editar"
                        aria-label={`Editar ${prop.nombre}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        className="gp__action-btn gp__action-btn--delete"
                        onClick={() => setPropiedadEliminando(prop)}
                        title={parseInt(prop.id) === 1 ? 'La propiedad principal no se puede eliminar' : 'Eliminar'}
                        aria-label={`Eliminar ${prop.nombre}`}
                        disabled={parseInt(prop.id) === 1}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalCrear && (
        <ModalPropiedad
          propiedad={null}
          onClose={() => setModalCrear(false)}
          onSaved={() => setModalCrear(false)}
        />
      )}

      {propiedadEditando && (
        <ModalPropiedad
          propiedad={propiedadEditando}
          onClose={() => setPropiedadEditando(null)}
          onSaved={() => setPropiedadEditando(null)}
        />
      )}

      {propiedadEliminando && (
        <ModalConfirmarEliminar
          propiedad={propiedadEliminando}
          onClose={() => setPropiedadEliminando(null)}
          onConfirm={handleEliminarConfirm}
          cargando={eliminando}
        />
      )}
    </>
  );
}

// ============================================================
// Tab 2: Asignaciones
// ============================================================

function TabAsignaciones() {
  const [propiedadSelId, setPropiedadSelId] = useState('');
  const [modalAsignar, setModalAsignar] = useState(false);

  const { data: dataPropiedades, loading: loadingProps } = useQuery(TODAS_PROPIEDADES);

  const { data: dataUsuarios, loading: loadingUsuarios, refetch: refetchUsuarios } = useQuery(
    USUARIOS_PROPIEDAD,
    {
      variables: { propiedad_id: parseInt(propiedadSelId) },
      skip: !propiedadSelId
    }
  );

  const [removerUsuario, { loading: removiendo }] = useMutation(REMOVER_USUARIO_PROPIEDAD, {
    onCompleted: () => {
      toast.success('Usuario removido de la propiedad');
      refetchUsuarios();
    },
    onError: (err) => toast.error('Error al remover usuario: ' + err.message)
  });

  const handleRemover = (usuarioId, nombre) => {
    if (!window.confirm(`Remover a "${nombre}" de esta propiedad?`)) return;
    removerUsuario({
      variables: {
        usuario_id: parseInt(usuarioId),
        propiedad_id: parseInt(propiedadSelId)
      }
    });
  };

  const propiedades = dataPropiedades?.todasPropiedades || [];
  const usuariosAsignados = dataUsuarios?.usuariosPropiedad || [];

  return (
    <>
      <div className="gp__section-header">
        <div>
          <h3 className="gp__section-title">Asignaciones de usuarios</h3>
          <p className="gp__section-subtitle">Gestiona que usuarios tienen acceso a cada propiedad</p>
        </div>
        {propiedadSelId && (
          <button
            className="gp__btn gp__btn--primary"
            onClick={() => setModalAsignar(true)}
          >
            <UserPlus size={16} />
            Asignar Usuario
          </button>
        )}
      </div>

      {/* Selector de propiedad */}
      <div className="gp__prop-selector">
        <label htmlFor="gp-prop-select" className="gp__prop-selector-label">
          <Building2 size={16} />
          Seleccionar Propiedad
        </label>
        {loadingProps ? (
          <p className="gp__loading-text">Cargando...</p>
        ) : (
          <div className="gp__select-wrap gp__select-wrap--large">
            <select
              id="gp-prop-select"
              value={propiedadSelId}
              onChange={(e) => setPropiedadSelId(e.target.value)}
            >
              <option value="">-- Selecciona una propiedad --</option>
              {propiedades.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}{p.ciudad ? ` - ${p.ciudad}` : ''}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="gp__select-icon" />
          </div>
        )}
      </div>

      {/* Tabla de usuarios */}
      {!propiedadSelId ? (
        <div className="gp__empty">
          <Users size={40} />
          <p className="gp__empty-title">Selecciona una propiedad</p>
          <p className="gp__empty-desc">Elige una propiedad del selector para ver sus usuarios asignados</p>
        </div>
      ) : loadingUsuarios ? (
        <div className="gp__state">
          <RefreshCw size={24} className="gp__spin" />
          <p>Cargando usuarios...</p>
        </div>
      ) : usuariosAsignados.length === 0 ? (
        <div className="gp__empty">
          <UserPlus size={40} />
          <p className="gp__empty-title">Sin usuarios asignados</p>
          <p className="gp__empty-desc">Asigna usuarios a esta propiedad con el boton "Asignar Usuario"</p>
        </div>
      ) : (
        <div className="gp__table-container">
          <table className="gp__table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Nombre</th>
                <th>Rol</th>
                <th>Estado Asignacion</th>
                <th className="gp__th--actions">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {usuariosAsignados.map((u) => (
                <tr key={u.id}>
                  <td data-label="Usuario">
                    <span className="gp__usuario-handle">@{u.usuario}</span>
                  </td>
                  <td data-label="Nombre">
                    {u.nombre} {u.apellido}
                  </td>
                  <td data-label="Rol">
                    <RolBadge rol={u.rol} />
                  </td>
                  <td data-label="Estado">
                    <EstadoBadge activo={u.asignacion_activa} />
                  </td>
                  <td data-label="Acciones">
                    <div className="gp__actions-cell">
                      <button
                        className="gp__action-btn gp__action-btn--delete"
                        onClick={() => handleRemover(u.id, `${u.nombre} ${u.apellido}`)}
                        title="Remover de esta propiedad"
                        aria-label={`Remover ${u.nombre} de la propiedad`}
                        disabled={removiendo}
                      >
                        <UserMinus size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalAsignar && (
        <ModalAsignarUsuario
          propiedadId={propiedadSelId}
          usuariosAsignados={usuariosAsignados}
          onClose={() => setModalAsignar(false)}
          onSaved={() => setModalAsignar(false)}
        />
      )}
    </>
  );
}

// ============================================================
// Page: GestionPropiedades
// ============================================================

export default function GestionPropiedades() {
  const [tabActiva, setTabActiva] = useState('propiedades');

  return (
    <div className="gp__page">
      {/* Page Header */}
      <div className="gp__header">
        <div className="gp__header-title-group">
          <h1 className="gp__title">
            <Building2 size={26} />
            Gestion de Propiedades
          </h1>
          <p className="gp__subtitle">
            Administra las propiedades del sistema y sus usuarios asignados
          </p>
        </div>
        <span className="gp__superadmin-badge">
          <CheckCircle size={12} />
          Solo Superadmin
        </span>
      </div>

      {/* Tabs */}
      <div className="gp__tabs" role="tablist">
        <button
          role="tab"
          aria-selected={tabActiva === 'propiedades'}
          className={`gp__tab ${tabActiva === 'propiedades' ? 'gp__tab--active' : ''}`}
          onClick={() => setTabActiva('propiedades')}
        >
          <Building2 size={15} />
          Propiedades
        </button>
        <button
          role="tab"
          aria-selected={tabActiva === 'asignaciones'}
          className={`gp__tab ${tabActiva === 'asignaciones' ? 'gp__tab--active' : ''}`}
          onClick={() => setTabActiva('asignaciones')}
        >
          <Users size={15} />
          Asignaciones
        </button>
      </div>

      {/* Tab content */}
      <div className="gp__content" role="tabpanel">
        {tabActiva === 'propiedades' && <TabPropiedades />}
        {tabActiva === 'asignaciones' && <TabAsignaciones />}
      </div>
    </div>
  );
}
